/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import { Signal, inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { OSKCurrentUserToken } from '../../injection-tokens/current-user.token';
import { OSKCurrentUser } from '../../types/user/current-user.type';
import { OSKUserAccount } from '../../types/user/user.type';

/**
 * Must be included after {@link OSKLoggedInGuard} in order to have the accounts and to be sure that the user is logged in. Otherwise it will prevent the navigation.
 *
 * Checks if the user has the permissions to access the route.
 *
 * @param route {@link ActivatedRouteSnapshot}
 * @param state {@link RouterStateSnapshot}
 * @returns { boolean } If the user has permissions to access the route (true/false)
 */
export const OSKUserRoleGuard: CanActivateFn = (
  route,
  state
): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree => {
  const currentUser: Signal<OSKCurrentUser> = inject(OSKCurrentUserToken);
  const router: Router = inject(Router);

  const rolesToCheck: string[] = route.data.userRoles || [];

  /**
   * The role v1.admin has access to everything in the OKSEY admin panel.
   * Adding it here allows us to omit it from the route's data
   */
  if (state.url.startsWith('/admin')) {
    rolesToCheck.unshift('v1.admin');
  }

  let selectedAccount: OSKUserAccount | undefined = undefined;
  const accounts = currentUser().oskUser?.accounts;

  /**
   * Since the selected account is only set after navigation end, this is mandatory when reloading the page.
   *
   * We already have the logged in user and its accounts(because we waited for the logged-in guard) so we can make the check here if we don't have the selectedAccount yet.
   */
  if (!selectedAccount && accounts && accounts.length > 0) {
    if (state.url == '/') {
      selectedAccount = accounts[0];
    } else if (state.url.startsWith('/osk-ui')) {
      selectedAccount =
        accounts.find((a) => a.roles.some((r) => rolesToCheck.includes(r))) ?? accounts[0];
    } else {
      selectedAccount = accounts.find((a) => state.url.startsWith(a.path));
    }
  }

  return rolesToCheck.some((r) => selectedAccount?.roles.includes(r)) ? true : router.parseUrl('/');
};
